<script setup lang="ts">
import { reactive, computed, watch, ref } from 'vue'
import type { FormInstance } from 'element-plus'
import type {
  RealTimeCurtailmentDecisionAttributes,
  AutoCurtailmentFormState,
} from '@/types/eos/AutoCurtailmentManagement'
import { useAutoCurtailmentManagement } from '@/composables/eos/auto-curtailment-management'
import ElCurrencyInput from '@/components/common/ElCurrencyInput.vue'
import { range } from 'lodash'
import { NotifyFail } from '@/helpers/core/notifications'

const open = defineModel<boolean>()
const props = defineProps<{
  site?: string
  attributes?: RealTimeCurtailmentDecisionAttributes | null
  mode?: 'create' | 'manage'
}>()

const emit = defineEmits<{
  (e: 'update'): void
}>()

const disabledMinutes = () => range(0, 60)
const disabledSeconds = () => range(0, 60)

const formRef = ref<FormInstance>()

const attributesMap = [
  {
    key: 'default_lmp_value',
    label: 'Default LMP value',
    prefix: '$/MWh',
  },
  {
    key: 'default_site_load_mwh',
    label: 'Default site load',
    prefix: 'MWh',
  },
  {
    key: 'expected_revenue_per_mwh',
    label: 'Revenue from customers',
    prefix: '$/MWh',
  },
  {
    key: 'curtailment_profit_threshold',
    label: 'Profit threshold',
    prefix: '$',
  },
  {
    key: 'curtailment_loss_threshold',
    label: 'Loss threshold',
    prefix: '$',
  },
  {
    key: 'iso_scheduling_fee_percentage',
    label: 'Scheduling fee',
    prefix: '%',
  },
  {
    key: 'iso_tax_percentage',
    label: 'Tax',
    prefix: '%',
  },
  {
    key: 'iso_adder_value_per_kwh',
    label: 'Adder value',
    prefix: '$/kWh',
    precision: 4,
  },
  {
    key: 'temperature_threshold_in_fahrenheit',
    label: 'Temperature threshold',
    prefix: '°F',
    allowNegative: true,
  },
  {
    key: 'lmp_spike_threshold',
    label: 'LMP spike threshold',
    prefix: '$',
    precision: 2,
  },
  {
    key: 'number_of_consecutive_lmp_spikes',
    label: 'Consecutive LMP spikes',
    prefix: '#',
    precision: 0,
  },
  {
    key: 'disable_rt_curtailments',
    label: null,
  },
  {
    key: 'curtail_on_custom_time_window',
    label: null,
  },
  {
    key: 'custom_curtailment_window_start_hour',
    label: null,
  },
  {
    key: 'custom_curtailment_window_end_hour',
    label: null,
  },
]

const getEmptyForm = (): AutoCurtailmentFormState => ({
  disable_rt_curtailments: false,
  default_lmp_value: null,
  default_site_load_mwh: null,
  expected_revenue_per_mwh: null,
  curtailment_profit_threshold: null,
  curtailment_loss_threshold: null,
  iso_scheduling_fee_percentage: null,
  iso_tax_percentage: null,
  iso_adder_value_per_kwh: null,
  temperature_threshold_in_fahrenheit: null,
  number_of_consecutive_lmp_spikes: null,
  lmp_spike_threshold: null,
  curtail_on_custom_time_window: false,
  custom_curtailment_window_start_hour: undefined,
  custom_curtailment_window_end_hour: undefined,
})

const form = reactive<AutoCurtailmentFormState>(getEmptyForm())

const rules = {
  default_lmp_value: [{ required: true, message: 'Default LMP value is required', trigger: 'blur' }],
  default_site_load_mwh: [{ required: true, message: 'Default site load is required', trigger: 'blur' }],
  expected_revenue_per_mwh: [{ required: true, message: 'Revenue from customers is required', trigger: 'blur' }],
  curtailment_profit_threshold: [{ required: true, message: 'Profit threshold is required', trigger: 'blur' }],
  curtailment_loss_threshold: [{ required: true, message: 'Loss threshold is required', trigger: 'blur' }],
  iso_scheduling_fee_percentage: [{ required: true, message: 'Scheduling fee is required', trigger: 'blur' }],
  iso_tax_percentage: [{ required: true, message: 'Tax is required', trigger: 'blur' }],
  iso_adder_value_per_kwh: [{ required: true, message: 'Adder value is required', trigger: 'blur' }],
  temperature_threshold_in_fahrenheit: [
    { required: true, message: 'Temperature threshold is required', trigger: 'blur' },
  ],
  lmp_spike_threshold: [{ required: true, message: 'LMP spike threshold is required', trigger: 'blur' }],
  number_of_consecutive_lmp_spikes: [
    { required: true, message: 'Consecutive LMP spikes is required', trigger: 'blur' },
    {
      validator: (rule: any, value: string, callback: any) => {
        const num = parseFloat(value)
        if (isNaN(num) || num < 1 || num > 10) {
          callback(new Error('Consecutive LMP spikes must be greater or equal than 1 and lesser or equal than 10'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
  custom_curtailment_window_start_hour: [
    {
      required: form.curtail_on_custom_time_window,
      message: 'Start hour is required when custom curtailment is enabled',
      trigger: 'change',
    },
  ],
  custom_curtailment_window_end_hour: [
    {
      required: form.curtail_on_custom_time_window,
      message: 'End hour is required when custom curtailment is enabled',
      trigger: 'change',
    },
  ],
}

// Watch for changes in attributes and dialog state to update form
watch(
  [open, () => props.attributes],
  ([isOpen, newAttributes]) => {
    if (isOpen && newAttributes) {
      attributesMap.forEach(({ key }) => {
        const typedKey = key as keyof AutoCurtailmentFormState
        if (typedKey === 'disable_rt_curtailments' || typedKey === 'curtail_on_custom_time_window') {
          form[typedKey] = newAttributes[typedKey] ?? false
        } else {
          form[typedKey] = newAttributes[typedKey]?.toString() ?? ''
        }
      })
    } else {
      // Reset form when dialog is closed or no attributes provided
      Object.assign(form, getEmptyForm())
    }
  },
  { immediate: true }
)

// Computed property for time range
const timeRange = computed({
  get: () => {
    if (!form.curtail_on_custom_time_window) return []
    return [form.custom_curtailment_window_start_hour!, form.custom_curtailment_window_end_hour!]
  },
  set: (value: string[]) => {
    if (value && value.length === 2) {
      form.custom_curtailment_window_start_hour = value[0]
      form.custom_curtailment_window_end_hour = value[1]
    } else {
      form.custom_curtailment_window_start_hour = ''
      form.custom_curtailment_window_end_hour = ''
    }
  },
})

const { updateRealTimeCurtailmentDecisionAttributesMutation, createRealTimeCurtailmentDecisionAttributesMutation } =
  useAutoCurtailmentManagement()

const { mutateAsync: updateMutateAsync, isPending: isUpdating } = updateRealTimeCurtailmentDecisionAttributesMutation
const { mutateAsync: createMutateAsync, isPending: isCreating } = createRealTimeCurtailmentDecisionAttributesMutation

const handleSave = async () => {
  if (!props.site || !formRef.value) return

  try {
    await formRef.value.validate()

    if (props.mode === 'create') {
      await createMutateAsync({
        siteId: props.site,
        form,
      })
    } else {
      await updateMutateAsync({
        siteId: props.site,
        form,
      })
    }
    emit('update')
    open.value = false
  } catch (error: any) {
    // Form validation error, don't close the dialog
    NotifyFail(`Failed to update auto curtailment settings: ${error.message}`)
  }
}

// Add a handler for the cancel button to reset validation
const handleCancel = () => {
  formRef.value?.resetFields()
  open.value = false
}
</script>

<template>
  <el-dialog
    v-model="open"
    :title="'Manage RT LMP Auto Curtailment - ' + site"
    :width="600"
    :show-close="false"
    top="5vh"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-position="left" label-width="auto">
      <el-form-item label="Automatic curtailment analysis">
        <div class="flex items-center gap-spacing-sm">
          <el-switch
            v-model="form.disable_rt_curtailments"
            :active-value="false"
            :inactive-value="true"
            active-text="On"
            inactive-text="Off"
            inline-prompt
          />
        </div>
      </el-form-item>

      <template v-for="attribute in attributesMap" :key="attribute.key">
        <el-form-item v-if="attribute.label" :label="attribute.label" :prop="attribute.key">
          <el-currency-input
            v-model="form[attribute.key as keyof Omit<AutoCurtailmentFormState, 'curtail_on_custom_time_window'>]"
            :prefix="attribute.prefix"
            :precision="attribute.precision ?? undefined"
            :clearable="true"
            :allow-negative="attribute.allowNegative"
            @blur="() => formRef?.validateField(attribute.key)"
          />
        </el-form-item>
      </template>

      <el-form-item label="Custom Curtailment Hours" label-position="right" class="hide-label">
        <el-checkbox v-model="form.curtail_on_custom_time_window" label="Custom Curtailment Hours" />
      </el-form-item>

      <template v-if="form.curtail_on_custom_time_window">
        <el-form-item label="Curtailment Hours" prop="custom_curtailment_window_start_hour" required>
          <el-time-picker
            v-model="timeRange"
            is-range
            range-separator="to"
            start-placeholder="Start time"
            end-placeholder="End time"
            format="H"
            value-format="H"
            :disabled-minutes="disabledMinutes"
            :disabled-seconds="disabledSeconds"
          />
        </el-form-item>
      </template>
    </el-form>

    <template #footer>
      <el-button @click="handleCancel">Cancel</el-button>
      <el-button type="primary" :loading="isUpdating || isCreating" @click="handleSave">{{
        mode === 'create' ? 'Create' : 'Save changes'
      }}</el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.el-input,
.el-select {
  width: 100%;
}

:deep(.el-input__prefix-inner) {
  margin-right: var(--spacing-spacing-sm);
}

:deep(.hide-label > .el-form-item__label-wrap) {
  visibility: hidden;
}

:deep(.el-time-picker) {
  width: 100%;
}
</style>
